import { Ionicons } from '@expo/vector-icons';
import { useEffect, useRef } from 'react';
import { Animated, Easing, Platform, StyleSheet, Text, View } from 'react-native';

interface Props {
  size?: number;
  /** Χρώμα του "αναμμένου" neon (π.χ. χρυσό για το κουδούνι, aqua για το chat). */
  color?: string;
  iconName: keyof typeof Ionicons.glyphMap;
  iconNameInactive: keyof typeof Ionicons.glyphMap;
  /** Όταν true: παίρνει το `color`, αναβοσβήνει με glow. Όταν false: γκρι,
   * ακίνητο — σαν σβηστή neon λάμπα. */
  active: boolean;
  /** Αριθμός για το badge πάνω δεξιά· εμφανίζεται μόνο όταν active και >0. */
  count?: number;
}

const INACTIVE_COLOR = 'rgba(148,163,184,0.55)';

/** Κοινό εικονίδιο-"neon λάμπα" για κουδούνι/chat: σβηστό+γκρι σε ηρεμία,
 * αναμμένο+αναβοσβήνει (glow pulse σε δύο επίπεδα + ελαφρύ scale) με το δικό
 * του χρώμα, και badge αριθμού πάνω δεξιά που κάνει το δικό του pop όταν
 * υπάρχει κάτι νέο. */
export function NeonNotifIcon({ size = 24, color = '#C9A843', iconName, iconNameInactive, active, count = 0 }: Props) {
  const pulse = useRef(new Animated.Value(0)).current;
  const badgePulse = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (!active) {
      pulse.setValue(0);
      badgePulse.setValue(0);
      return;
    }
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 750, easing: Easing.out(Easing.sin), useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(pulse, { toValue: 0, duration: 750, easing: Easing.in(Easing.sin), useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    anim.start();

    const badgeAnim = Animated.loop(
      Animated.sequence([
        Animated.delay(150),
        Animated.timing(badgePulse, { toValue: 1, duration: 260, easing: Easing.out(Easing.back(2)), useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(badgePulse, { toValue: 0, duration: 260, easing: Easing.inOut(Easing.quad), useNativeDriver: Platform.OS !== 'web' }),
        Animated.delay(1050),
      ]),
    );
    badgeAnim.start();

    return () => {
      anim.stop();
      badgeAnim.stop();
    };
  }, [active, pulse, badgePulse]);

  // Δύο επάλληλοι glow κύκλοι (φαρδύς+αχνός / στενός+πιο έντονος) για πιο
  // "μαλακό" neon halo αντί για έναν επίπεδο δίσκο.
  const outerGlowOpacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.08, 0.35] });
  const outerGlowScale = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.85, 1.15] });
  const innerGlowOpacity = pulse.interpolate({ inputRange: [0, 1], outputRange: [0.2, 0.55] });
  const iconScale = pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.08] });
  const badgeScale = badgePulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.25] });

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      {active && (
        <>
          <Animated.View
            pointerEvents="none"
            style={{
              position: 'absolute',
              width: size * 2.4,
              height: size * 2.4,
              borderRadius: size * 1.2,
              backgroundColor: color,
              opacity: outerGlowOpacity,
              transform: [{ scale: outerGlowScale }],
            }}
          />
          <Animated.View
            pointerEvents="none"
            style={{
              position: 'absolute',
              width: size * 1.5,
              height: size * 1.5,
              borderRadius: size * 0.75,
              backgroundColor: color,
              opacity: innerGlowOpacity,
            }}
          />
        </>
      )}
      <Animated.View style={{ transform: [{ scale: active ? iconScale : 1 }] }}>
        <Ionicons name={active ? iconName : iconNameInactive} size={size} color={active ? color : INACTIVE_COLOR} />
      </Animated.View>
      {active && count > 0 && (
        <Animated.View style={[styles.badge, { transform: [{ scale: badgeScale }] }]}>
          <Text style={styles.badgeText}>{count > 9 ? '9+' : count}</Text>
        </Animated.View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    position: 'absolute',
    top: -4,
    right: -6,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 3,
    backgroundColor: '#DC2626',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#0D1B2A',
  },
  badgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '800',
  },
});
